import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useLiveParticipantId } from '../hooks/useLiveParticipantId';
import { getResult } from '../utils/liveApi';
import { topNFurthest } from '../utils/ternary';
import TernaryTriangle from '../components/live/TernaryTriangle';
import questionsData from '../data/orientation-axis-questions.json';

export default function LiveResultPage() {
  const { code: rawCode } = useParams();
  const code = (rawCode || '').toUpperCase();
  const participantId = useLiveParticipantId(code);

  const [state, setState] = useState({ loading: true, error: null, data: null });

  useEffect(() => {
    if (!code || !participantId) return undefined;
    let alive = true;
    setState({ loading: true, error: null, data: null });
    getResult({ code, participantId })
      .then((data) => { if (alive) setState({ loading: false, error: null, data }); })
      .catch((e) => { if (alive) setState({ loading: false, error: e, data: null }); });
    return () => { alive = false; };
  }, [code, participantId]);

  const axesLabels = useMemo(
    () => ({
      innocence: questionsData.axes[0].shortLabel,
      honneur: questionsData.axes[1].shortLabel,
      puissance: questionsData.axes[2].shortLabel
    }),
    []
  );

  const { furthest, highlightLabels } = useMemo(() => {
    if (!state.data || !state.data.myPosition || state.data.myPosition.x == null) {
      return { furthest: [], highlightLabels: [] };
    }
    const top = topNFurthest(state.data.myPosition, state.data.allPositions || [], 2);
    return { furthest: top, highlightLabels: top.map((p) => p.label) };
  }, [state.data]);

  if (state.loading) {
    return (
      <div className="container" style={{ marginTop: '2rem', textAlign: 'center' }}>
        <div className="loading">Chargement de votre résultat…</div>
      </div>
    );
  }

  if (state.error) {
    const status = state.error?.response?.status;
    const err = state.error?.response?.data?.error;
    return (
      <div className="container" style={{ marginTop: '2rem' }}>
        <h2>Résultat indisponible</h2>
        {status === 409 && err === 'session_not_ended' && <p>La session n'est pas encore terminée.</p>}
        {status === 404 && <p>Session ou participant introuvable.</p>}
        {!status && <p>{state.error.message}</p>}
        <Link to="/" className="btn btn-secondary">Retour à l'accueil</Link>
      </div>
    );
  }

  const { myPosition, allPositions, centroid } = state.data;

  if (!myPosition || myPosition.n === 0 || myPosition.x == null) {
    return (
      <div className="container" style={{ marginTop: '2rem' }}>
        <h2>Vous n'avez répondu à aucune question</h2>
        <p>Pas de position à afficher.</p>
        <Link to="/" className="btn btn-secondary">Retour à l'accueil</Link>
      </div>
    );
  }

  const pct = (v) => `${Math.round(v * 100)}%`;
  const total = allPositions ? allPositions.length : 0;

  return (
    <div className="container" style={{ marginTop: '1.5rem', maxWidth: 720, display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <h1 style={{ margin: 0 }}>Votre position</h1>
      <p style={{ color: '#7f8c8d', margin: 0 }}>
        Session {code} · {total} participant{total > 1 ? 's' : ''} · {myPosition.n} question{myPosition.n > 1 ? 's' : ''} répondue{myPosition.n > 1 ? 's' : ''}
      </p>

      <div className="card" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <TernaryTriangle
          points={allPositions || []}
          centroid={centroid}
          meLabel={myPosition.label}
          highlightLabels={highlightLabels}
          axesLabels={axesLabels}
          size={460}
        />
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', fontSize: '0.85rem', color: '#7f8c8d', marginTop: '0.5rem' }}>
          <span><span style={{ color: '#e74c3c' }}>●</span> vous</span>
          <span><span style={{ color: '#f39c12' }}>●</span> les 2 personnes les plus éloignées de vous</span>
          <span><span style={{ color: '#3498db' }}>●</span> autres participants</span>
          <span><span style={{ color: '#27ae60' }}>○</span> moyenne du groupe</span>
        </div>
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Votre composition</h3>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          {[0, 1, 2].map((axisIdx) => (
            <div key={axisIdx} style={{ flex: 1, minWidth: 160, padding: '0.75rem', background: '#f8f9fa', borderRadius: 6 }}>
              <div style={{ fontSize: '0.85rem', color: '#7f8c8d' }}>{questionsData.axes[axisIdx].label}</div>
              <div style={{ fontSize: '1.6rem', fontWeight: 700, color: '#2c3e50' }}>
                {pct(myPosition.bary[axisIdx])}
              </div>
              <div style={{ fontSize: '0.8rem', color: '#bdc3c7' }}>
                {myPosition.counts[axisIdx]} réponse{myPosition.counts[axisIdx] > 1 ? 's' : ''}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Les deux personnes les plus éloignées de vous</h3>
        {furthest.length === 0 && <p>Pas assez de participants pour comparer.</p>}
        {furthest.length > 0 && (
          <ol style={{ paddingLeft: '1.25rem' }}>
            {furthest.map((p) => (
              <li key={p.label} style={{ marginBottom: '0.4rem' }}>
                <strong>{p.label}</strong> — distance {(p.d).toFixed(2)} ·{' '}
                <span style={{ color: '#7f8c8d' }}>
                  {pct(p.bary[0])} innocence · {pct(p.bary[1])} honneur · {pct(p.bary[2])} puissance
                </span>
              </li>
            ))}
          </ol>
        )}
      </div>

      <Link to="/" className="btn btn-secondary" style={{ alignSelf: 'flex-start' }}>Retour à l'accueil</Link>
    </div>
  );
}
